import { Form } from "@inertiajs/react"
import { Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import SyncProgressTicker from "@/components/brand/settings/products/sync-progress-ticker"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { formatDateTime } from "@/lib/format-datetime"
import {
  brandSettingsIntegrationsPath,
  brandSettingsProductsSyncPath,
} from "@/routes"
import type { ProductsStore } from "@/types/products"

/**
 * OKS-931 — The SyncPanel: catalogue health, the counts, and the ONE re-sync
 * control in the app (UX §3.3 / §3.4).
 *
 * ## Two surfaces, one truth (PRD D7)
 *
 * The failure alert resolves `settings.brand.integrations.shopify.errors.*` —
 * the **OKS-930 namespace, reused verbatim**, never a copy. The Integrations
 * card renders the same key off the same column, so the two cannot disagree
 * by construction. Copying the six strings into this ticket's namespace would
 * create a second alphabet that LQE's four-place drift spec does not cover.
 *
 * ## The status line no longer keys on `sync_in_progress` (OKS-960)
 *
 * ⚠️ The OKS-931 rule — "the boolean drives the running branch" — is REPLACED,
 * not annotated. The running branch now reads `store.sync_progress`, a durable
 * server-side fact carrying the page and the counters, because a boolean cannot
 * say how far along an import is. `sync_in_progress` survives only as the
 * disabled-button label.
 *
 * ## The ticker is optimistic on submit, and MUST be able to retract
 *
 * `running` is derived — `progress !== null || processing` — and holds no state
 * of its own. That is the mechanism, not a shortcut: `processing` is scoped to
 * the round trip, so when the server REFUSES (the throttle is the reachable
 * case) the response carries `sync_progress: null`, `processing` falls false,
 * and the ticker disappears on the same render that shows the throttle toast.
 *
 * A `useState(true)` set on submit would need a correct reset on every failure
 * path — refusal, validation error, network error, navigation — and the one
 * that gets missed is exactly the "ticker survives a refusal" defect this
 * surface exists to remove. Deriving makes the wrong state unreachable rather
 * than guarded.
 *
 * ## The catalogue is never blanked by a failure (UX §3.3)
 *
 * A failed sync shows the error AND the previously-synced products AND the
 * last SUCCESSFUL timestamp. A brand must never be unable to tell whether
 * what they are looking at is current.
 */
interface BodyProps {
  store: ProductsStore
  syncedAt: string | null
  failed: boolean
  throttled: boolean
  throttleMinutes: number
  canManage: boolean
  hasProducts: boolean
  live: boolean
  onRefresh: () => void
  refreshing: boolean
}

export default function SyncPanel({
  store,
  resyncAvailableInSeconds,
  canManage,
  hasProducts,
  live,
  onRefresh,
  refreshing,
}: {
  store: ProductsStore
  resyncAvailableInSeconds: number | null
  canManage: boolean
  hasProducts: boolean
  /** Signals are actually arriving for THIS viewer (AC19). */
  live: boolean
  onRefresh: () => void
  refreshing: boolean
}) {
  // Only the language is needed here — every string lives in `PanelBody`.
  const { i18n } = useTranslation()

  const syncedAt = formatDateTime(store.last_synced_at, i18n.language)
  const failed = store.status === "error"
  const throttled =
    resyncAvailableInSeconds !== null && resyncAvailableInSeconds > 0
  // Server-authoritative. Ceil to whole minutes, matching the flash the
  // server emits when it refuses — the button and the toast must not
  // disagree about the same number.
  const throttleMinutes = throttled
    ? Math.ceil(resyncAvailableInSeconds / 60)
    : 0

  const bodyProps: BodyProps = {
    store,
    syncedAt,
    failed,
    throttled,
    throttleMinutes,
    canManage,
    hasProducts,
    live,
    onRefresh,
    refreshing,
  }

  // ⚠️ The Form wraps the BODY so the status line can read `processing`.
  //
  // The optimistic ticker must retract on a server refusal, and the mechanism
  // for that is deriving `running` from `processing` rather than holding
  // submit state (see the class doc). `processing` only exists inside the
  // Inertia `<Form>` render prop, so the form has to enclose the status line —
  // it cannot sit below it.
  //
  // A member who cannot manage has no submit control at all, so for them there
  // is no form and `processing` is permanently false. Same body either way; the
  // wrapper is the only difference.
  //
  // The look-again button inside the body is `type="button"`, so it cannot
  // submit this form.
  return (
    <Card data-testid="brand-products-sync-panel">
      {canManage ? (
        <Form
          action={brandSettingsProductsSyncPath()}
          method="post"
          options={{ preserveScroll: true }}
        >
          {({ processing }) => (
            <PanelBody {...bodyProps} pending={processing} />
          )}
        </Form>
      ) : (
        <PanelBody {...bodyProps} pending={false} />
      )}
    </Card>
  )
}

/** Everything inside the card. Split out purely so it can be rendered either
 *  inside the sync `<Form>` (managers) or bare (everyone else) without
 *  duplicating the markup. */
function PanelBody({
  store,
  syncedAt,
  failed,
  throttled,
  throttleMinutes,
  canManage,
  hasProducts,
  live,
  onRefresh,
  refreshing,
  pending,
}: BodyProps & { pending: boolean }) {
  const { t } = useTranslation()

  // Derived, never held. `sync_progress` is the server's durable fact;
  // `pending` is scoped to the round trip. A refusal returns null progress and
  // settles `pending`, so the ticker retracts on the same render.
  // ⚠️ `!= null` (loose), so an absent prop reads as "not running" rather than
  // as a running import with nothing to show. See the note in
  // `SyncProgressTicker`: strict-null admits `undefined` and the ticker then
  // dereferences it.
  const running = store.sync_progress != null || pending

  return (
    <>
      <CardContent className="space-y-4">
        {/* Status line, priority order — first match renders (UX §3.4). */}
        <p
          className="text-muted-foreground text-sm"
          data-testid="brand-products-sync-status"
        >
          {running ? (
            <SyncProgressTicker
              progress={store.sync_progress}
              pending={pending}
              live={live && store.sync_progress?.started_by_viewer === true}
              onRefresh={onRefresh}
              refreshing={refreshing}
            />
          ) : syncedAt === null ? (
            t("settings.brand.products.sync.never")
          ) : (
            <>
              {t("settings.brand.products.sync.at", { at: syncedAt })}
              {store.last_sync_product_count !== null ? (
                <>
                  {" · "}
                  <span data-testid="brand-products-count">
                    {t("settings.brand.products.sync.product_count", {
                      count: store.last_sync_product_count,
                    })}
                  </span>
                </>
              ) : null}
              {store.last_sync_variant_count !== null ? (
                <>
                  {" · "}
                  <span data-testid="brand-products-variant-count">
                    {t("settings.brand.products.sync.variant_count", {
                      count: store.last_sync_variant_count,
                    })}
                  </span>
                </>
              ) : null}
            </>
          )}
        </p>

        {/*
          Always present. Without it the manual button reads as the only way
          the catalogue ever updates, and the weekly cadence is stated nowhere
          else in the app (UX §3.4).
        */}
        <p className="text-muted-foreground text-xs">
          {t("settings.brand.products.sync.auto_hint")}
        </p>

        {failed ? (
          <Alert
            variant="destructive"
            role="alert"
            data-testid="brand-products-sync-error"
          >
            <AlertTitle>
              {t("settings.brand.products.sync.failed_title")}
            </AlertTitle>
            <AlertDescription className="space-y-1">
              {/*
                The OKS-930 namespace, reused verbatim (PRD D7/D10). Six
                reasons, no seventh — the copy that exists is already correct
                at sync time.
              */}
              <span>
                {store.last_error_reason
                  ? t(
                      `settings.brand.integrations.shopify.errors.${store.last_error_reason}`,
                    )
                  : null}
              </span>
              {hasProducts ? (
                <span data-testid="brand-products-stale-notice">
                  {t("settings.brand.products.sync.failed_stale")}
                </span>
              ) : null}
              <Link
                href={brandSettingsIntegrationsPath()}
                className="underline underline-offset-4"
              >
                {t("settings.brand.products.sync.failed_link")}
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}

        {store.last_sync_truncated ? (
          /*
            `variant="default"` because `Alert` has exactly two variants and
            adding a third would be an un-ledgered `ui/` hand-edit (95-BLOCK).
            Deliberately NOT destructive: the import SUCCEEDED.

            ⚠️ `role="status"` is a required OVERRIDE, not decoration. The
            `Alert` primitive hardcodes `role="alert"` and spreads `{...props}`
            only afterwards, so WITHOUT this line the truncation notice is an
            ASSERTIVE live region that interrupts a screen reader — which UX §6
            forbids precisely because nothing here is urgent. The Vitest
            example caught this; the override has to stay for it to pass.
          */
          <Alert
            variant="default"
            role="status"
            data-testid="brand-products-truncated"
          >
            <AlertDescription>
              {t("settings.brand.products.sync.truncated", {
                count: store.last_sync_product_count ?? 0,
              })}
            </AlertDescription>
          </Alert>
        ) : null}

        {/*
          DOM-ABSENT when the member cannot manage — not disabled, and with no
          explanatory line. A member who never had the control is not confused
          by its absence, and a sentence about permissions would be the only
          copy on this page that talks about anything but products (UX §3.4).
        */}
        {canManage ? (
          <div
            className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end"
            data-testid="brand-products-actions"
          >
            {throttled && !store.sync_in_progress ? (
              <span
                className="text-muted-foreground text-sm"
                data-testid="brand-products-throttle-note"
              >
                {t("settings.brand.products.sync.throttled", {
                  minutes: throttleMinutes,
                })}
              </span>
            ) : null}

            {/* No nested <Form> — the body is already inside the sync form
                (see the note above). Nested forms are invalid HTML and the
                inner one would swallow the submit. */}
            <Button
              type="submit"
              className="h-11 w-full sm:h-9 sm:w-auto"
              data-testid="brand-products-sync-button"
              /*
                An affordance, never the guard — the server refuses
                independently (AC8) and the refusal renders as the warning
                toast with the same minute count.
              */
              disabled={store.sync_in_progress || throttled || pending}
            >
              {store.sync_in_progress
                ? t("settings.brand.products.sync.in_progress")
                : pending
                  ? t("settings.brand.products.sync.starting")
                  : t("settings.brand.products.sync.now")}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </>
  )
}
